import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import {
  TestOpsPlanInterval,
} from '../../../../models';
import KsePurchaseDetails from './KsePurchaseDetails';
import KrePurchaseDetails from './KrePurchaseDetails';
import TestCloudPurchaseDetails from './TestCloudPurchaseDetails';
import VisualTestingPurchaseDetails from './VisualTestingPurchaseDetails';
import K1Typography from '../../../../components/design-system/K1Typography';
import PlatformPurchaseDetails from './PlatformPurchaseDetails';
import { useQuery } from '../../../../routes';
import { useLaunchDarkly } from '../../../../launchdarkly';
import { fromTestOpsPlatformSubscriptions } from '../../../../store';

const useStyles = makeStyles(theme => ({
  groupTitle: {
    fontSize: 12,
    fontWeight: theme.typography.fontWeightBold,
    padding: theme.spacing(2, 0, 0, 0),
    color: '#727993',
  },
}));

export interface PlatformOfferingSectionProps{
  accountId: number;
  initialPlatformQuota?: number;
  initialKseQuota?: number;
  initialKreQuota?: number;
  initialTestCloudQuota?: number;
  initialVisualTestingQuota?: number;
  clearInitialQuota: Function;
}

const PlatformOfferingSection = (props: PlatformOfferingSectionProps) => {
  const classes = useStyles();
  const {
    accountId,
    initialPlatformQuota,
    initialKseQuota,
    initialKreQuota,
    initialTestCloudQuota,
    initialVisualTestingQuota,
    clearInitialQuota,
  } = props;
  const { get } = useQuery();
  const { flags } = useLaunchDarkly();
  const platformCycle = TestOpsPlanInterval.fromString(get('platformCycle') || '');

  const currentPlatformSubscription = useSelector(
    fromTestOpsPlatformSubscriptions.selectPaidTestOpsPlatformSubscriptionByAccountId(
      Number(accountId),
    ),
  )?.[0];

  const getInitialPlatformBillingCycle = () => {
    if (currentPlatformSubscription
      && currentPlatformSubscription?.billingCycle === TestOpsPlanInterval.YEAR) {
      return TestOpsPlanInterval.YEAR;
    }

    if (platformCycle) {
      return platformCycle;
    }
    return currentPlatformSubscription?.billingCycle || TestOpsPlanInterval.YEAR;
  };

  return (
    <>
      <Box id="platform">
        <K1Typography variant="h3" fontWeight="medium" className={classes.groupTitle}>
          <FormattedMessage id="subscriptions.testops_platform.offering.test_management" />
        </K1Typography>
        <PlatformPurchaseDetails
          accountId={accountId}
          currentSubscription={currentPlatformSubscription}
          initialInterval={getInitialPlatformBillingCycle()}
          initialQuota={initialPlatformQuota}
          clearInitialQuota={clearInitialQuota}
        />
      </Box>
      <Box id="platform-kse">
        <K1Typography variant="h3" fontWeight="medium" className={classes.groupTitle}>
          <FormattedMessage id="subscriptions.testops_platform.offering.test_creation" />
        </K1Typography>
        <KsePurchaseDetails
          accountId={accountId}
          initialQuota={initialKseQuota}
          title={<FormattedMessage id="subscriptions.kse.title" />}
          clearInitialQuota={clearInitialQuota}
        />
      </Box>
      <Box id="platform-kre">
        <K1Typography variant="h3" fontWeight="medium" className={classes.groupTitle}>
          <FormattedMessage id="subscriptions.testops_platform.offering.test_execution" />
        </K1Typography>
        <KrePurchaseDetails
          accountId={accountId}
          initialQuota={initialKreQuota}
          title={<FormattedMessage id="subscriptions.kre.title" />}
          clearInitialQuota={clearInitialQuota}
        />
      </Box>
      <Box id="test_cloud">
        <TestCloudPurchaseDetails
          accountId={accountId}
          initialQuota={initialTestCloudQuota}
          title={<FormattedMessage id="subscriptions.test_cloud.title" />}
          clearInitialQuota={clearInitialQuota}
        />
      </Box>
      {flags && !flags.unlimitedVTPEnabled && (
        <Box id="visual_testing">
          <K1Typography variant="h3" fontWeight="medium" className={classes.groupTitle}>
            <FormattedMessage id="subscriptions.testops_platform.offering.add_on" />
          </K1Typography>
          <VisualTestingPurchaseDetails
            accountId={accountId}
            initialQuota={initialVisualTestingQuota}
            title={<FormattedMessage id="subscriptions.visual_testing.title" />}
            clearInitialQuota={clearInitialQuota}
          />
        </Box>
      )}
      {flags && !flags.unlimitedVTPEnabled && <Divider />}
    </>
  );
};

export default PlatformOfferingSection;
